/**
 * Seans notu.
 *
 * Taslak hâlindeyken serbestçe düzenleniyor; **Kilitle** dendiğinde
 * kayıt dondurulıyor ve ondan sonra düzeltme yalnızca ek not olarak
 * yazılabiliyor. Kilidi açma düğmesi YOK: açılabilen bir kilit,
 * kaydın sonradan sessizce değiştirilebileceği anlamına gelirdi ve
 * notun güvenilirliğini bitirirdi.
 *
 * Zorunlu alan da yok. Seans içinde not tutan bir uygulayıcıyı zorunlu
 * alanla durdurmak, notun hiç yazılmamasına yol açıyor; eksik not,
 * olmayan nottan iyidir.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  ApiHatasi,
  SEANS_TURU,
  api,
  type NotSablonu,
  type NotSurumu,
  type SeansDosyasi,
} from '../api'

function anTarih(s: string | null): string {
  if (!s) return '—'
  const d = new Date(s)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' })
}

export function SeansNotu({
  seansId,
  onGeri,
}: {
  seansId: string
  onGeri: () => void
}) {
  const [dosya, setDosya] = useState<SeansDosyasi | null>(null)
  const [sablonlar, setSablonlar] = useState<Record<string, NotSablonu> | null>(null)
  const [sablon, setSablon] = useState('followup')
  const [alanlar, setAlanlar] = useState<Record<string, string>>({})
  const [metin, setMetin] = useState('')
  const [ekMetin, setEkMetin] = useState('')
  const [surumler, setSurumler] = useState<NotSurumu[] | null>(null)
  const [hata, setHata] = useState<string | null>(null)
  const [bilgi, setBilgi] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)
  const [kilitOnayi, setKilitOnayi] = useState(false)

  const yukle = useCallback(async () => {
    setHata(null)
    try {
      const d = await api.seans(seansId)
      setDosya(d)
      if (d.not) {
        setSablon(d.not.sablon)
        setAlanlar(d.not.alanlar)
        setMetin(d.not.metin)
      } else {
        // Seansın türü şablonu belirliyor; ölçüm oturumunda takip
        // şablonu en yakını
        setSablon(d.seans.tur === 'assessment' ? 'followup' : d.seans.tur)
      }
    } catch {
      setHata('Seans açılamadı.')
    }
  }, [seansId])

  useEffect(() => {
    void yukle()
  }, [yukle])

  useEffect(() => {
    api
      .notSablonlari()
      .then((r) => setSablonlar(r.sablonlar))
      .catch(() => setSablonlar(null))
  }, [])

  if (hata && !dosya) {
    return (
      <>
        <div className="uyari">{hata}</div>
        <button onClick={onGeri}>← Geri</button>
      </>
    )
  }
  if (!dosya || !sablonlar) return <p className="alt">Yükleniyor…</p>

  const kilitli = Boolean(dosya.not?.kilitli)
  const tanim = sablonlar[sablon]

  const kaydet = async () => {
    setHata(null)
    setBilgi(null)
    setBekliyor(true)
    try {
      const r = await api.notKaydet(seansId, { sablon, alanlar, metin })
      await yukle()
      setBilgi(`Kaydedildi (sürüm ${r.not.surum}).`)
    } catch (e) {
      setHata(
        e instanceof ApiHatasi && e.kod === 'not_kilitli'
          ? 'Bu not kilitli; düzeltmeyi ek not olarak yazın.'
          : 'Kaydedilemedi.',
      )
    } finally {
      setBekliyor(false)
    }
  }

  const kilitle = async () => {
    setHata(null)
    setBekliyor(true)
    try {
      // Önce yazılanı kaydet: kilitlenen şey ekrandaki metin olmalı
      await api.notKaydet(seansId, { sablon, alanlar, metin })
      await api.notKilitle(dosya.not?.id ?? (await api.seans(seansId)).not!.id)
      await yukle()
      setBilgi('Not kilitlendi. Düzeltme artık ek not olarak yazılır.')
    } catch {
      setHata('Kilitlenemedi.')
    } finally {
      setBekliyor(false)
      setKilitOnayi(false)
    }
  }

  const ekEkle = async () => {
    if (!dosya.not || !ekMetin.trim()) return
    setHata(null)
    setBekliyor(true)
    try {
      await api.notEk(dosya.not.id, ekMetin.trim())
      setEkMetin('')
      await yukle()
      setBilgi('Ek not yazıldı.')
    } catch {
      setHata('Ek not yazılamadı.')
    } finally {
      setBekliyor(false)
    }
  }

  const surumleriAc = async () => {
    if (!dosya.not) return
    try {
      const r = await api.notSurumleri(dosya.not.id)
      setSurumler(r.surumler)
    } catch {
      setHata('Sürümler alınamadı.')
    }
  }

  return (
    <>
      <div className="dugmeler" style={{ marginBottom: 12 }}>
        <button onClick={onGeri}>← Dosya</button>
        {kilitli && <button onClick={() => window.print()}>Yazdır</button>}
      </div>

      <h1>{SEANS_TURU[dosya.seans.tur] ?? dosya.seans.tur}</h1>
      <p className="alt">
        {anTarih(dosya.seans.baslangic)}
        {dosya.not ? ` · ${dosya.not.surumSayisi} sürüm` : ' · not yok'}
        {kilitli ? ` · kilitlendi ${anTarih(dosya.not!.kilitli)}` : ''}
      </p>

      {hata && <div className="uyari">{hata}</div>}
      {bilgi && <div className="bilgi">{bilgi}</div>}

      {kilitli && (
        <div className="bilgi">
          Bu not kilitli ve değiştirilemez. Düzeltme gerekiyorsa aşağıya ek not yazın; özgün
          metin olduğu gibi kalır.
        </div>
      )}

      {!kilitli && (
        <div className="kart">
          <label style={{ marginBottom: 0 }}>
            Şablon
            <select
              value={sablon}
              onChange={(e) => {
                setSablon(e.target.value)
                setAlanlar({})
              }}
            >
              {Object.entries(sablonlar).map(([kod, s]) => (
                <option key={kod} value={kod}>
                  {s.ad}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {tanim?.alanlar.map((a) => (
        <div className="kart" key={a.anahtar}>
          <label style={{ marginBottom: 0 }}>
            {a.etiket}
            {a.ipucu && (
              <span className="alt" style={{ margin: 0, fontSize: 12 }}>
                {a.ipucu}
              </span>
            )}
            <textarea
              rows={a.satir}
              readOnly={kilitli}
              value={alanlar[a.anahtar] ?? ''}
              onChange={(e) => setAlanlar({ ...alanlar, [a.anahtar]: e.target.value })}
            />
          </label>
        </div>
      ))}

      <div className="kart">
        <label style={{ marginBottom: 0 }}>
          Serbest not
          <textarea
            rows={4}
            readOnly={kilitli}
            value={metin}
            onChange={(e) => setMetin(e.target.value)}
          />
        </label>
      </div>

      {!kilitli && (
        <div className="dugmeler">
          <button className="birincil" disabled={bekliyor} onClick={() => void kaydet()}>
            {bekliyor ? 'Bekleyin…' : 'Kaydet'}
          </button>
          {kilitOnayi ? (
            <>
              <button disabled={bekliyor} onClick={() => void kilitle()}>
                Evet, kilitle — geri alınamaz
              </button>
              <button disabled={bekliyor} onClick={() => setKilitOnayi(false)}>
                Vazgeç
              </button>
            </>
          ) : (
            <button disabled={bekliyor || !dosya.not} onClick={() => setKilitOnayi(true)}>
              Kilitle
            </button>
          )}
        </div>
      )}

      {dosya.ekler.length > 0 && (
        <>
          <h2>Ek notlar</h2>
          {dosya.ekler.map((e) => (
            <div className="kart" key={e.id}>
              <div className="alt" style={{ margin: '0 0 6px' }}>
                {e.yazan ?? '—'} · {anTarih(e.tarih)}
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{e.metin}</div>
            </div>
          ))}
        </>
      )}

      {kilitli && (
        <div className="kart">
          <label>
            Ek not
            <textarea
              rows={3}
              value={ekMetin}
              onChange={(e) => setEkMetin(e.target.value)}
              placeholder="Düzeltme ya da sonradan eklenen bilgi"
            />
          </label>
          <div className="dugmeler">
            <button
              className="birincil"
              disabled={bekliyor || !ekMetin.trim()}
              onClick={() => void ekEkle()}
            >
              Ek notu yaz
            </button>
          </div>
        </div>
      )}

      {dosya.not && (
        <>
          <div className="dugmeler" style={{ marginTop: 16 }}>
            <button onClick={() => (surumler ? setSurumler(null) : void surumleriAc())}>
              {surumler ? 'Sürümleri gizle' : 'Sürüm geçmişi'}
            </button>
          </div>
          {surumler && (
            <table style={{ marginTop: 10 }}>
              <thead>
                <tr>
                  <th>Sürüm</th>
                  <th>Kaydeden</th>
                  <th>Tarih</th>
                </tr>
              </thead>
              <tbody>
                {surumler.map((s) => (
                  <tr key={s.surum}>
                    <td>{s.surum}</td>
                    <td>{s.yazan ?? '—'}</td>
                    <td style={{ color: 'var(--muted)' }}>{anTarih(s.tarih)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </>
  )
}
